var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable react-hooks/exhaustive-deps */
import { isBool, isEqual, isNum, isValid } from '@formily/shared';
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FormGridContext } from './context';
const S = 720;
const MD = 1280;
const LG = 1920;
const defaultProps = {
    columnGap: 10,
    rowGap: 5,
    minColumns: 1,
    maxColumns: 999,
    minWidth: 100,
    breakpoints: [S, MD, LG],
    colWrap: true,
};
const normalizeProps = (originalProps) => {
    const props = Object.assign(Object.assign({}, defaultProps), originalProps);
    const { breakpoints } = props;
    const intervals = breakpoints.reduce((buf, cur, index, array) => {
        if (index === array.length - 1) {
            return [...buf, [array[index], Infinity]];
        }
        if (index === 0) {
            return [
                [0, cur],
                [cur, array[index + 1]],
            ];
        }
        return [...buf, [cur, array[index + 1]]];
    }, []);
    const normalize = (prop) => {
        if (isNum(prop) || isBool(prop)) {
            return intervals.map(() => prop);
        }
        else if (Array.isArray(prop)) {
            let lastVal;
            return intervals.map((it, idx) => {
                const res = (isValid(prop[idx]) ? prop[idx] : lastVal) || 0;
                lastVal = isValid(prop[idx]) ? prop[idx] : lastVal;
                return res;
            });
        }
        else {
            return prop;
        }
    };
    return Object.assign(Object.assign({}, props), { intervals, colWrap: normalize(props.colWrap), minWidth: normalize(props.minWidth), maxWidth: normalize(props.maxWidth), minColumns: normalize(props.minColumns), maxColumns: normalize(props.maxColumns) });
};
const useGridLayout = (outerProps) => {
    // const formGridPrefixCls = usePrefixCls('formily-grid', outerProps);
    const [layoutParams, setLayout] = useState({});
    const [styles, setStyles] = useState({});
    const normalizedProps = normalizeProps(outerProps);
    const props = useRef(normalizedProps);
    const ref = useRef(null);
    const calculateSmartColumns = (target) => {
        var _a;
        const { clientWidth } = target;
        const index = props.current.intervals.findIndex((interval) => {
            const [min, max] = interval;
            if (clientWidth >= min && max > clientWidth) {
                return true;
            }
            return false;
        });
        const takeMaxColumns = () => {
            return props.current.maxColumns[index];
        };
        const takeMinColumns = () => {
            return props.current.minColumns[index] || 1;
        };
        const takeColwrap = () => {
            var _a;
            return ((_a = props.current.colWrap) === null || _a === void 0 ? void 0 : _a[index]) || props.current.colWrap || true;
        };
        const takeMinWidth = () => {
            const rMaxColumns = takeMaxColumns();
            if (isValid(props.current.minWidth)) {
                return Array.isArray(props.current.minWidth) ? props.current.minWidth[index] : props.current.minWidth || 0;
            }
            else {
                if (isValid(rMaxColumns)) {
                    return Math.floor((clientWidth - (rMaxColumns - 1) * props.current.columnGap) / rMaxColumns);
                }
                else {
                    return 0;
                }
            }
        };
        const takeMaxWidth = () => {
            var _a;
            if (isValid(props.current.maxWidth)) {
                return props.current.maxWidth[index] || 0;
            }
            else {
                if (isValid((_a = props.current.minColumns) === null || _a === void 0 ? void 0 : _a[index])) {
                    const calculated = Math.floor((clientWidth - (props.current.minColumns[index] - 1) * props.current.columnGap) /
                        props.current.minColumns[index]);
                    if (Infinity === calculated) {
                        return clientWidth;
                    }
                    return calculated;
                }
                else {
                    return Infinity;
                }
            }
        };
        return {
            minWidth: takeMinWidth(),
            maxWidth: takeMaxWidth(),
            columns: (_a = target === null || target === void 0 ? void 0 : target.childNodes) === null || _a === void 0 ? void 0 : _a.length,
            colWrap: takeColwrap(),
            minColumns: takeMinColumns(),
            maxColumns: takeMaxColumns(),
            clientWidth,
        };
    };
    const updateUI = () => {
        if (ref.current) {
            const params = calculateSmartColumns(ref.current);
            setLayout(params);
            const style = getStyle({
                columnGap: props.current.columnGap,
                rowGap: props.current.rowGap,
                layoutParams: params,
                ref: ref,
            });
            if (!isEqual(style, styles)) {
                setStyles(style);
            }
        }
    };
    useEffect(() => {
        updateUI();
    }, [
        outerProps.minWidth,
        outerProps.maxWidth,
        outerProps.minColumns,
        outerProps.maxColumns,
        outerProps.columnGap,
        outerProps.rowGap,
    ]);
    useLayoutEffect(() => {
        const observer = () => {
            updateUI();
        };
        const resizeObserver = new ResizeObserver(observer);
        const mutationObserver = new MutationObserver(observer);
        if (ref.current) {
            resizeObserver.observe(ref.current);
            mutationObserver.observe(ref.current, {
                childList: true,
            });
        }
        updateUI();
        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, []);
    return {
        ref: ref,
        // formGridPrefixCls,
        layoutParams,
        styles,
    };
};
const getStyle = (props) => {
    var _a;
    const { columnGap = defaultProps.columnGap, rowGap = defaultProps.rowGap, layoutParams, ref } = props;
    const { clientWidth, minWidth, maxColumns, minColumns } = layoutParams;
    const getMinMax = (minWidth, maxWidth) => {
        let minmax;
        if (minWidth === Infinity) {
            if (!isValid(maxWidth)) {
                minmax = '1fr';
            }
            else {
                minmax = `minmax(0px,${maxWidth}px)`;
            }
        }
        else {
            minmax = `minmax(${minWidth}px,${isValid(maxWidth) ? `${maxWidth}px` : '1fr'})`;
        }
        return minmax;
    };
    const spans = Array.from(((_a = ref.current) === null || _a === void 0 ? void 0 : _a.childNodes) || []).reduce((buf, cur) => {
        const span = isValid(maxColumns)
            ? Math.min((Number(cur.getAttribute('data-span')) || 1), maxColumns)
            : Number(cur.getAttribute('data-span')) || 1;
        return buf + Number(span);
    }, 0);
    const calc = Math.floor((clientWidth + columnGap) / (minWidth + columnGap));
    let finalColumns;
    finalColumns = Math.min(spans, calc);
    if (isValid(maxColumns)) {
        finalColumns = Math.min(spans, calc, maxColumns);
    }
    else {
        finalColumns = Math.min(spans, calc);
    }
    if (isValid(minColumns)) {
        if (finalColumns < minColumns) {
            finalColumns = minColumns;
        }
    }
    const style = {
        gridTemplateColumns: `repeat(${finalColumns}, ${getMinMax(layoutParams.minWidth, layoutParams.maxWidth)})`,
        gridGap: `${rowGap}px ${columnGap}px`,
    };
    return style;
};
export const useGridSpan = (gridSpan = 1) => {
    const params = useContext(FormGridContext);
    if (!isValid(params)) {
        return gridSpan;
    }
    const { colWrap, columns = 1, clientWidth, minWidth, columnGap, maxColumns = defaultProps.maxColumns } = params;
    const calc = Math.floor((clientWidth + columnGap) / (minWidth + columnGap)); // 算出实际一行最多能塞进的格子数
    if (colWrap === true) {
        if (Math.min(calc, columns) >= gridSpan) {
            if (isValid(maxColumns)) {
                return Math.min(gridSpan, maxColumns);
            }
            return gridSpan;
        }
        else {
            if (isValid(maxColumns)) {
                return Math.min(calc, gridSpan, maxColumns);
            }
            return Math.min(calc, gridSpan);
        }
    }
    else {
        if (Math.min(calc, columns) >= gridSpan) {
            if (isValid(maxColumns)) {
                return Math.min(gridSpan, maxColumns);
            }
            return gridSpan;
        }
        else {
            if (isValid(maxColumns)) {
                return Math.min(calc, columns, maxColumns);
            }
            return Math.min(calc, columns);
        }
    }
};
export const pickDataProps = (props = {}) => {
    const results = {};
    for (let key in props) {
        if (key.indexOf('data-') > -1) {
            results[key] = props[key];
        }
    }
    return results;
};
export const FormGrid = (props) => {
    const { children } = props;
    const { ref, layoutParams, styles } = useGridLayout(props);
    const dataProps = pickDataProps(props);
    return (_jsx(FormGridContext.Provider, Object.assign({ value: Object.assign({ columnGap: props.columnGap }, layoutParams) }, { children: _jsx("div", Object.assign({}, dataProps, { 
            // className={cls(`${formGridPrefixCls}-layout`, props.className)}
            style: Object.assign(Object.assign({ display: 'grid' }, props.style), styles), ref: ref }, { children: children })) })));
};
export const GridColumn = (_a) => {
    var { gridSpan, children } = _a, props = __rest(_a, ["gridSpan", "children"]);
    const span = FormGrid.useGridSpan(gridSpan);
    return (_jsx("div", Object.assign({}, props, { style: Object.assign(Object.assign({}, props.style), { gridColumnStart: `span ${span}` }), "data-span": span || 1 }, { children: children })));
};
GridColumn.defaultProps = {
    gridSpan: 1,
};
FormGrid.useGridSpan = useGridSpan;
FormGrid.GridColumn = GridColumn;
FormGrid.defaultProps = defaultProps;
export default FormGrid;
